import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button, IconButton, Tooltip } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import {
  deleteCorporateEmployee,
  getCorporateEmployees,
  getCostCenterOptions,
  getPolicyOptions,
} from '../../../../api/endpoints/corporateEmployee';
import type { CorporateEmployee } from '../../../../api/types/corporateEmployee';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import {
  ListPageScaffold,
  RowActions,
  StatusChip,
  useDeleteResource,
  type DataTableColumn,
} from '../../../../components/common';
import { EmployeeFormDialog } from '../components/EmployeeFormDialog';
import { ExcelImportDialog } from '../components/ExcelImportDialog';
import { ManagerAssignDialog } from '../components/ManagerAssignDialog';

/** Personel Listesi — arama client-side (`GetCorporateEmployeeByQuery` sunucuda kırık,
 *  bkz. api/types/corporateEmployee.ts). */
export default function EmployeeListPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<CorporateEmployee | null>(null);
  const [assigningManagerFor, setAssigningManagerFor] = useState<CorporateEmployee | null>(null);
  const [importOpen, setImportOpen] = useState(false);

  const query = useQuery({ queryKey: ['corporateEmployee', 'list'], queryFn: () => getCorporateEmployees() });
  const policiesQuery = useQuery({ queryKey: ['corporateEmployee', 'policyOptions'], queryFn: () => getPolicyOptions() });
  const costCentersQuery = useQuery({
    queryKey: ['corporateEmployee', 'costCenterOptions'],
    queryFn: () => getCostCenterOptions(),
  });

  const employees: CorporateEmployee[] = useMemo(() => query.data?.corporateEmployeeList ?? [], [query.data]);
  const policies = policiesQuery.data?.policies ?? [];
  const costCenters = costCentersQuery.data?.costCenter ?? [];

  const rows = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('tr');
    if (!term) return employees;
    return employees.filter((e) =>
      [e.name, e.surName, e.emailAdress, e.registrationNumber, e.phoneNumber]
        .filter(Boolean)
        .some((field) => field!.toLocaleLowerCase('tr').includes(term))
    );
  }, [employees, search]);

  const del = useDeleteResource<CorporateEmployee>({
    mutationFn: (employee) => deleteCorporateEmployee(employee),
    invalidateKeys: [['corporateEmployee', 'list']],
    successMessage: 'Personel silindi.',
  });

  const columns: DataTableColumn<CorporateEmployee>[] = [
    { id: 'name', header: 'Ad Soyad', cell: (r) => `${r.name ?? ''} ${r.surName ?? ''}`.trim() },
    { id: 'email', header: 'E-posta', cell: (r) => r.emailAdress ?? '—' },
    { id: 'policy', header: 'Politika', cell: (r) => r.policyName || '—' },
    { id: 'costCenter', header: 'Masraf Merkezi', cell: (r) => r.costCenterName || '—' },
    { id: 'manager', header: 'Yönetici', cell: (r) => r.managerName || '—' },
    { id: 'status', header: 'Durum', cell: (r) => <StatusChip active={r.isActive} /> },
    {
      id: 'kvkk',
      header: 'KVKK',
      cell: (r) => (
        <StatusChip tone={r.kvkkAccepted ? 'success' : 'warning'} label={r.kvkkAccepted ? 'Onaylı' : 'Bekliyor'} />
      ),
    },
  ];

  return (
    <ListPageScaffold
      title="Kurumsal Personel"
      createLabel="Yeni Personel"
      onCreate={() => setCreateOpen(true)}
      headerActions={
        <Button variant="outlined" startIcon={<UploadFileIcon />} onClick={() => setImportOpen(true)}>
          Excel'den İçe Aktar
        </Button>
      }
      search={{ value: search, onChange: setSearch, placeholder: 'Ad, soyad, e-posta, sicil no veya telefon ara…' }}
      query={query}
      rows={rows}
      columns={columns}
      getRowKey={(r) => r.employeeID}
      emptyTitle="Kayıtlı personel bulunamadı."
      rowActions={(r) => (
        <RowActions
          onEdit={() => setEditing(r)}
          onDelete={() => del.request(r)}
          leading={
            <>
              <Tooltip title="Detay">
                <IconButton size="small" onClick={() => navigate(`/corporate/employees/${r.employeeID}`)} aria-label="Detay">
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Yönetici Ata">
                <IconButton size="small" onClick={() => setAssigningManagerFor(r)} aria-label="Yönetici Ata">
                  <SupervisorAccountIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          }
        />
      )}
    >
      <EmployeeFormDialog open={createOpen} employee={null} policies={policies} costCenters={costCenters} onClose={() => setCreateOpen(false)} />
      <EmployeeFormDialog open={!!editing} employee={editing} policies={policies} costCenters={costCenters} onClose={() => setEditing(null)} />
      <ManagerAssignDialog
        open={!!assigningManagerFor}
        employee={assigningManagerFor}
        candidates={employees}
        onClose={() => setAssigningManagerFor(null)}
      />
      <ExcelImportDialog open={importOpen} onClose={() => setImportOpen(false)} />
      <ConfirmDialog
        {...del.confirmDialogProps}
        title="Personeli sil"
        description={`"${del.target?.name ?? ''} ${del.target?.surName ?? ''}" adlı personeli silmek istediğinize emin misiniz?`}
      />
    </ListPageScaffold>
  );
}
